import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Icon, type IconName } from './icons';

/**
 * A dropdown menu.
 *
 * Replaces the `<details>` element the header menu used to be. A details menu looks the
 * part and behaves wrongly in three ways that matter: it stays open when you click
 * elsewhere, it ignores Escape, and it announces itself as a disclosure widget rather
 * than a menu. All three are the kind of thing you only notice by trying to dismiss it.
 *
 * Focus returns to the trigger on close, so keyboard users are not dropped at the top of
 * the document after choosing something.
 */
export function Menu({
  trigger, label, align = 'end', width = 'w-60', children,
}: {
  /** Rendered inside the trigger button. */
  trigger: ReactNode;
  /** Accessible name for the trigger. */
  label: string;
  align?: 'start' | 'end';
  width?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const onPointer = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative" ref={wrap}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center rounded-full transition-colors"
      >
        {trigger}
      </button>

      {open && (
        <div
          id={id}
          role="menu"
          aria-label={label}
          className={clsx(
            'absolute z-40 mt-2 overflow-hidden rounded-lg border border-[var(--ff-border)]',
            'bg-[var(--ff-surface)] p-1.5 shadow-[var(--ff-shadow-lg)]',
            width,
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  );
}

/** A row in a Menu. Renders as a button, or as whatever `as` provides. */
export function MenuItem({
  icon, children, onClick, tone = 'default',
}: {
  icon?: IconName;
  children: ReactNode;
  onClick?: () => void;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm',
        'transition-colors',
        tone === 'danger'
          ? 'text-[var(--ff-danger)] hover:bg-[var(--ff-danger-soft)]'
          : 'text-[var(--ff-text)] hover:bg-[var(--ff-surface-raised)]',
      )}
    >
      {icon && <Icon name={icon} size={15} className="shrink-0 text-[var(--ff-text-muted)]" />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p className="truncate px-2.5 py-1.5 text-xs text-[var(--ff-text-muted)]">{children}</p>
  );
}

export function MenuSeparator() {
  return <hr className="my-1 border-0 border-t border-[var(--ff-border)]" />;
}
